import { useEffect, useRef, useState } from "react";

/* One engine for the whole page. Everything that wants to know where the page
   is subscribes to it, because eight components each attaching their own
   listener is how a site starts dropping frames on a mid-range Android.

   It reads the scroll position on a frame loop rather than listening for the
   scroll event. Two reasons, and the second is the one that matters: a frame
   loop is already the cadence the animation needs, and scroll events are not
   guaranteed to arrive on every frame during momentum scrolling on iOS. A
   pinned section driven by an event that arrives late judders; one driven by
   the frame it is about to paint does not. The loop costs two property reads
   and exits immediately when nothing has moved, and it stops altogether when
   the tab is hidden. */
const subs = new Set();
let raf = 0;
let lastY = -1;
let lastW = -1;
let lastH = -1;

function frame() {
  const y = window.scrollY;
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (y !== lastY || w !== lastW || h !== lastH) {
    lastY = y;
    lastW = w;
    lastH = h;
    subs.forEach((fn) => {
      try {
        fn();
      } catch (e) {
        /* one bad subscriber must not stop the rest of the page moving */
      }
    });
  }
  raf = requestAnimationFrame(frame);
}

function start() {
  if (raf || document.hidden) return;
  lastY = -1;
  raf = requestAnimationFrame(frame);
}
function stop() {
  if (!raf) return;
  cancelAnimationFrame(raf);
  raf = 0;
}
function onVisibility() {
  if (document.hidden) stop();
  else if (subs.size) start();
}

function subscribe(fn) {
  if (!subs.size) {
    document.addEventListener("visibilitychange", onVisibility);
    start();
  }
  subs.add(fn);
  fn();
  return () => {
    subs.delete(fn);
    if (!subs.size) {
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    }
  };
}

export const clamp01 = (n) => (n < 0 ? 0 : n > 1 ? 1 : n);

/** Reveal on first entry, then stop watching. Nothing un-reveals on the way
 *  back up: content that fades out as you scroll away reads as a bug.
 *
 *  This runs on the same frame loop as everything else rather than on an
 *  IntersectionObserver. The failure mode of a reveal is the worst one on the
 *  site, an entire section that never becomes visible, so it is worth having
 *  exactly one mechanism that is either working for everything or working for
 *  nothing. The stylesheet also starts .rv visible and only hides it once
 *  <html> has picked up the rv-on class, so a page whose JavaScript never runs
 *  is a page with all of its words on it. */
export function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const targets = Array.from(el.querySelectorAll(".rv"));
    if (!targets.length) return undefined;

    let left = targets.length;
    let unsub = null;
    const check = () => {
      const h = window.innerHeight;
      for (let i = 0; i < targets.length; i += 1) {
        const t = targets[i];
        if (!t || t.classList.contains("in")) continue;
        const r = t.getBoundingClientRect();
        // in view, or already scrolled past, which happens on a deep link
        if (r.top < h * 0.9 && r.bottom > -h) {
          t.classList.add("in");
          left -= 1;
        }
      }
      if (left <= 0 && unsub) {
        unsub();
        unsub = null;
      }
    };
    unsub = subscribe(check);
    return () => {
      if (unsub) unsub();
    };
  }, []);
  return ref;
}

/** Progress through a tall element while its sticky child is pinned, 0 to 1.
 *
 *  Everything the section shows, the artwork and the words, is driven by the
 *  custom properties written here. Nothing re-renders while you scroll. That is
 *  partly for the frame budget, but mostly so the copy and the artwork cannot
 *  disagree: they read the same numbers, so a beat that moves the phone also
 *  moves the paragraph, by construction rather than by both being wired up
 *  correctly.
 *
 *  Per beat i it writes:
 *    --bi   raw progress through that beat, 0 to 1
 *    --fi   the same thing but reaching 1 early, for things that should be on
 *           rather than arriving
 *    --oi   1 while that beat is the active one, 0 otherwise. CSS does the
 *           crossfade with a transition, so it stays a single source of truth.
 */
export function useArc(beats) {
  const trackRef = useRef(null);
  const pinRef = useRef(null);

  useEffect(() => {
    const track = trackRef.current;
    const pin = pinRef.current;
    if (!track || !pin) return undefined;

    return subscribe(() => {
      const r = track.getBoundingClientRect();
      const travel = r.height - pin.offsetHeight;
      const p = travel <= 0 ? 0 : clamp01(-r.top / travel);
      const active = Math.min(beats - 1, Math.floor(p * beats + 0.0001));

      pin.style.setProperty("--p", p.toFixed(4));
      for (let i = 0; i < beats; i += 1) {
        const local = clamp01(p * beats - i);
        pin.style.setProperty(`--b${i}`, local.toFixed(4));
        // clamped here rather than with clamp() in CSS, which does not survive
        // the build's PostCSS pass on unitless numbers
        pin.style.setProperty(`--f${i}`, clamp01((local - 0.04) * 2.6).toFixed(4));
        pin.style.setProperty(`--o${i}`, i === active ? "1" : "0");
      }
    });
  }, [beats]);

  return { trackRef, pinRef };
}

/** True once the page has moved off the very top. Used by the nav. */
export function useStuck(after = 12) {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    let was = false;
    return subscribe(() => {
      const is = window.scrollY > after;
      if (is !== was) {
        was = is;
        setStuck(is);
      }
    });
  }, [after]);
  return stuck;
}

/** Which section id is currently under the top of the viewport. */
export function useActiveSection(ids) {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    let was = null;
    return subscribe(() => {
      const y = window.scrollY + window.innerHeight * 0.34;
      let found = ids[0];
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= y) found = id;
      }
      if (found !== was) {
        was = found;
        setActive(found);
      }
    });
  }, [ids]);
  return active;
}

/** Small parallax: writes --y on the element as it crosses the viewport. */
export function useParallax(strength = 40) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    return subscribe(() => {
      const r = el.getBoundingClientRect();
      const mid = (r.top + r.height / 2 - window.innerHeight / 2) / window.innerHeight;
      el.style.setProperty("--y", `${(-mid * strength).toFixed(1)}px`);
    });
  }, [strength]);
  return ref;
}

/** Fires once, the first time the element is on screen. On the shared frame
 *  loop like everything else: a raw scroll listener here was the one place
 *  left that could silently never fire. */
export function useOnceInView(ref, fn, margin = 0.75) {
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof fn !== "function") return undefined;
    let done = false;
    let unsub = null;
    unsub = subscribe(() => {
      if (done) return;
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * margin && r.bottom > 0) {
        done = true;
        fn();
        if (unsub) {
          unsub();
          unsub = null;
        }
      }
    });
    return () => {
      if (unsub) unsub();
    };
  }, [ref, fn, margin]);
}
